import { signOut } from "firebase/auth";
import {
  Check,
  ChevronDown,
  ClipboardList,
  Eye,
  FileText,
  Home,
  Languages,
  LogOut,
  Route,
  UserRound,
  type LucideIcon
} from "lucide-react";
import { useEffect, useState, type FC } from "react";
import {
  Navigate,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext
} from "react-router-dom";

import { auth } from "@/api/firebaseApi";
import { LoadingState } from "@/components/brand";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle
} from "@/components/ui/empty";
import { queryClient } from "@/lib/query/queryClient";
import { cn } from "@/lib/utils";

import {
  initialPortalLanguage,
  languageDirection,
  PORTAL_LANGUAGES,
  portalI18n,
  rememberPortalLanguage,
  usePortalT
} from "../i18n";
import { initials, isTaskReady } from "../portalModel";
import {
  clientPortalPreview,
  endClientPortalPreview,
  PREVIEW_PARAM,
  startClientPortalPreview
} from "../preview";
import {
  CLIENT_PORTAL_KEY,
  useClientPortalHome,
  useClientPortalMe
} from "../queries";
import {
  CLIENT_PORTAL_CASE_PATH,
  CLIENT_PORTAL_DOCUMENTS_PATH,
  CLIENT_PORTAL_PATH,
  CLIENT_PORTAL_PROFILE_PATH,
  CLIENT_PORTAL_SIGN_IN_PATH,
  CLIENT_PORTAL_TASKS_PATH,
  rememberPortalReturn
} from "../routes";
import type { PortalFirm, PortalMe } from "../types";

export const useClientPortal = (): PortalMe => useOutletContext<PortalMe>();

type Tab = {
  path: string;
  label: string;
  short: string;
  icon: LucideIcon;
  end?: boolean;
};

const useTabs = (): Tab[] => {
  const { t } = usePortalT();
  return [
    {
      path: CLIENT_PORTAL_PATH,
      label: t("shell.nav.home"),
      short: t("shell.nav.home"),
      icon: Home,
      end: true
    },
    {
      path: CLIENT_PORTAL_CASE_PATH,
      label: t("shell.nav.case"),
      short: t("shell.nav.caseShort"),
      icon: Route
    },
    {
      path: CLIENT_PORTAL_TASKS_PATH,
      label: t("shell.nav.tasks"),
      short: t("shell.nav.tasks"),
      icon: ClipboardList
    },
    {
      path: CLIENT_PORTAL_DOCUMENTS_PATH,
      label: t("shell.nav.documents"),
      short: t("shell.nav.documents"),
      icon: FileText
    },
    {
      path: CLIENT_PORTAL_PROFILE_PATH,
      label: t("shell.nav.profile"),
      short: t("shell.nav.profileShort"),
      icon: UserRound
    }
  ];
};

/** Starts a preview from `?preview=<clientId>`; true while one runs. */
const usePreview = (search: string): boolean =>
  useState(() => {
    const param = new URLSearchParams(search).get(PREVIEW_PARAM);
    if (param && param !== clientPortalPreview()) {
      startClientPortalPreview(param);
      queryClient.removeQueries({ queryKey: CLIENT_PORTAL_KEY });
    }
    return Boolean(clientPortalPreview());
  })[0];

const exitPreview = (clientId: string | undefined): void => {
  endClientPortalPreview();
  queryClient.removeQueries({ queryKey: CLIENT_PORTAL_KEY });
  window.location.assign(
    clientId
      ? `/clients/${encodeURIComponent(clientId)}?tab=overview&portal=1`
      : "/clients"
  );
};

const useSignedIn = (): boolean | null => {
  const [signedIn, setSignedIn] = useState<boolean | null>(
    auth.currentUser ? true : null
  );
  useEffect(() => auth.onAuthStateChanged((u) => setSignedIn(Boolean(u))), []);
  return signedIn;
};

export const signOutOfClientPortal = async (): Promise<void> => {
  localStorage.removeItem("loginType");
  localStorage.removeItem("userId");
  queryClient.clear();
  await signOut(auth);
};

/** Picks the language once the client is known; keeps `dir` in step. */
const useLanguage = (preferred: string | null | undefined): string => {
  const { i18n } = usePortalT();
  useEffect(() => {
    if (preferred === undefined) return;
    void portalI18n.changeLanguage(initialPortalLanguage(preferred));
  }, [preferred]);
  return i18n.language;
};

export const FirmMark: FC<{ firm: PortalFirm; size?: "sm" | "md" }> = ({
  firm,
  size = "md"
}) => {
  const box = size === "sm" ? "size-7 text-xs" : "size-8 text-sm";
  if (firm.logo_url) {
    return (
      <img
        src={firm.logo_url}
        alt=""
        className={cn(box, "shrink-0 rounded-md object-contain")}
      />
    );
  }
  return (
    <span
      className={cn(
        box,
        "flex shrink-0 items-center justify-center rounded-md bg-primary font-bold text-primary-foreground"
      )}
    >
      {initials(firm.name) || "·"}
    </span>
  );
};

export const LanguageMenu: FC<{
  compact?: boolean;
  /** Controlled use (the intake): the language shown and what picking one does. */
  value?: string;
  onChange?: (code: string) => void;
  className?: string;
}> = ({ compact, value, onChange, className }) => {
  const { t, i18n } = usePortalT();
  const current =
    PORTAL_LANGUAGES.find((l) => l.code === (value ?? i18n.language)) ??
    PORTAL_LANGUAGES[0];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "h-8 rounded-full px-2.5 text-xs font-medium",
            className
          )}
          aria-label={t("shell.language")}
          data-testid="portal-language"
        >
          <Languages className="size-3.5" />
          {compact ? null : current.name}
          <ChevronDown className="size-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel>{t("shell.language")}</DropdownMenuLabel>
        {PORTAL_LANGUAGES.map((l) => (
          <DropdownMenuItem
            key={l.code}
            lang={l.code}
            onSelect={() => {
              if (onChange) {
                onChange(l.code);
                return;
              }
              rememberPortalLanguage(l.code);
              void portalI18n.changeLanguage(l.code);
            }}
          >
            <span className="flex-1">{l.name}</span>
            {l.code === current.code ? <Check className="size-4" /> : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const AccountMenu: FC<{ me: PortalMe }> = ({ me }) => {
  const { t } = usePortalT();
  const navigate = useNavigate();
  const name = [me.client.first_name, me.client.last_name]
    .filter(Boolean)
    .join(" ");
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={name}
          className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary"
        >
          {initials(name)}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="flex flex-col">
          <span>{name}</span>
          {me.client.email ? (
            <span className="text-xs font-normal text-muted-foreground">
              {me.client.email}
            </span>
          ) : null}
        </DropdownMenuLabel>
        {me.preview ? null : (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() =>
                void signOutOfClientPortal().then(() =>
                  navigate(CLIENT_PORTAL_SIGN_IN_PATH, { replace: true })
                )
              }
            >
              <LogOut className="size-4" />
              {t("shell.signOut")}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const TaskBadge: FC<{ count: number; className?: string }> = ({
  count,
  className
}) =>
  count > 0 ? (
    <span
      className={cn(
        "inline-flex h-4.25 min-w-4.25 items-center justify-center rounded-full bg-primary px-1 text-xs font-semibold text-primary-foreground",
        className
      )}
    >
      {count}
    </span>
  ) : null;

const Header: FC<{ me: PortalMe; taskCount: number }> = ({ me, taskCount }) => {
  const { t } = usePortalT();
  const tabs = useTabs();
  return (
    <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border bg-background px-4 md:h-14.5 md:gap-4 md:px-7">
      <span className="flex min-w-0 items-center gap-2.5">
        <FirmMark firm={me.firm} />
        <span className="flex min-w-0 flex-col leading-tight">
          <span className="truncate text-sm font-semibold">{me.firm.name}</span>
          <span className="text-xs text-muted-foreground">
            {t("shell.portal")}
          </span>
        </span>
      </span>
      <nav
        className="ms-4 hidden items-center gap-0.5 md:flex"
        aria-label={t("shell.portal")}
      >
        {tabs.map((tab) => (
          <NavLink
            key={tab.path}
            to={tab.path}
            end={tab.end}
            className={({ isActive }) =>
              cn(
                "inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-sm",
                isActive
                  ? "bg-muted font-semibold text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )
            }
          >
            {tab.label}
            {tab.path === CLIENT_PORTAL_TASKS_PATH ? (
              <TaskBadge count={taskCount} />
            ) : null}
          </NavLink>
        ))}
      </nav>
      <span className="flex-1" />
      <span className="hidden md:inline-flex">
        <LanguageMenu />
      </span>
      <span className="md:hidden">
        <LanguageMenu compact />
      </span>
      <AccountMenu me={me} />
    </header>
  );
};

const BottomTabs: FC<{ taskCount: number }> = ({ taskCount }) => {
  const tabs = useTabs();
  const { t } = usePortalT();
  return (
    <nav
      aria-label={t("shell.portal")}
      className="grid shrink-0 grid-cols-5 border-t border-border bg-background pb-[env(safe-area-inset-bottom)] md:hidden"
    >
      {tabs.map((tab) => (
        <NavLink
          key={tab.path}
          to={tab.path}
          end={tab.end}
          className={({ isActive }) =>
            cn(
              "relative flex flex-col items-center gap-1 py-2 text-xs",
              isActive ? "font-semibold text-primary" : "text-muted-foreground"
            )
          }
        >
          <tab.icon className="size-5" />
          {tab.short}
          {tab.path === CLIENT_PORTAL_TASKS_PATH ? (
            <TaskBadge
              count={taskCount}
              className="absolute end-[calc(50%-20px)] top-1"
            />
          ) : null}
        </NavLink>
      ))}
    </nav>
  );
};

const PreviewBar: FC<{ me: PortalMe }> = ({ me }) => {
  const { t } = usePortalT();
  return (
    <div className="flex shrink-0 items-center gap-3 border-b border-warn-line bg-warn-soft px-4 py-2 text-base text-warn md:px-7">
      <Eye className="size-4 shrink-0" />
      <span className="min-w-0 flex-1">
        {t("shell.previewing", { name: me.client.first_name })}
      </span>
      <Button
        size="sm"
        variant="outline"
        className="h-7 bg-background"
        onClick={() => exitPreview(me.client.uid)}
      >
        {t("shell.exitPreview")}
      </Button>
    </div>
  );
};

const Centered: FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex h-dvh items-center justify-center p-6">{children}</div>
);

const Portal: FC<{ me: PortalMe }> = ({ me }) => {
  const language = useLanguage(me.client.preferred_language ?? null);
  const home = useClientPortalHome();
  const taskCount = home.data?.tasks.filter(isTaskReady).length ?? 0;
  return (
    <div
      className="flex h-dvh flex-col bg-fill-1"
      dir={languageDirection(language)}
      lang={language}
      data-testid="client-portal-shell"
    >
      {me.preview ? <PreviewBar me={me} /> : null}
      <Header me={me} taskCount={taskCount} />
      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex w-full max-w-270 flex-col gap-5 px-4 py-5 md:px-7 md:py-8">
          <Outlet context={me} />
        </div>
      </main>
      <BottomTabs taskCount={taskCount} />
    </div>
  );
};

/** The client portal frame. Signed-out visitors go to the email sign-in; a
 * signed-in user who isn't a client is told so, not let in. */
export const ClientPortalShell: FC = () => {
  const { t } = usePortalT();
  const signedIn = useSignedIn();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const previewing = usePreview(search);
  const me = useClientPortalMe(signedIn === true);

  if (signedIn === null || (signedIn && me.isLoading)) {
    return (
      <Centered>
        <LoadingState size="xl" />
      </Centered>
    );
  }
  if (!signedIn) {
    rememberPortalReturn(pathname);
    return <Navigate to={CLIENT_PORTAL_SIGN_IN_PATH} replace />;
  }
  if (!me.data && previewing) {
    return (
      <Centered>
        <Empty className="max-w-md border border-border">
          <EmptyHeader>
            <EmptyTitle>{t("shell.cantPreview")}</EmptyTitle>
            <EmptyDescription>{me.error?.message}</EmptyDescription>
          </EmptyHeader>
          <Button
            variant="outline"
            onClick={() => exitPreview(clientPortalPreview() ?? undefined)}
          >
            {t("shell.exitPreview")}
          </Button>
        </Empty>
      </Centered>
    );
  }
  if (!me.data) {
    return (
      <Centered>
        <Empty className="max-w-md border border-border">
          <EmptyHeader>
            <EmptyTitle>{t("shell.notClient")}</EmptyTitle>
            <EmptyDescription>{t("shell.notClientBody")}</EmptyDescription>
          </EmptyHeader>
          <Button
            variant="outline"
            onClick={() =>
              void signOutOfClientPortal().then(() =>
                navigate(CLIENT_PORTAL_SIGN_IN_PATH, { replace: true })
              )
            }
          >
            {t("shell.useDifferentEmail")}
          </Button>
        </Empty>
      </Centered>
    );
  }
  return <Portal me={me.data} />;
};
